import type { Member } from '@familywallet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { Button, buttonVariants } from '../components/ui/button';
import { ChevronRight } from 'lucide-react';
import { Card, CardTitle, IconSquare, ListGroup, listRowClass } from '../components/ui/card';
import { api } from '../lib/api';
import { useCan, useMe } from '../lib/auth';
import { useLimits } from '../lib/ledger';
import { invitationsKey, useMembers } from '../lib/members';
import { OverBadge } from '../components/PlanningCards';

function MemberRow({
  member,
  isMe,
  overLimit,
}: {
  member: Member;
  isMe: boolean;
  overLimit: boolean;
}) {
  const { t } = useTranslation();
  const badges = [
    t(`roles.${member.role}`),
    member.hasLogin ? t('members.canSignIn') : t('members.noLogin'),
    ...(member.privateAccountsAllowed ? [t('members.privateAllowed')] : []),
    ...(member.status === 'deactivated' ? [t('members.deactivated')] : []),
  ];
  return (
    <li>
      <Link to={`/members/${member.id}`} className={listRowClass}>
        <IconSquare>{[...member.displayName][0]?.toLocaleUpperCase()}</IconSquare>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-[15px] font-medium">
            {member.displayName}
            {isMe && <span className="ms-2 text-sm text-muted">({t('members.you')})</span>}
          </span>
          <span className="text-xs text-muted">{badges.join(' · ')}</span>
        </span>
        {overLimit && <OverBadge label={t('limits.over')} />}
        <ChevronRight aria-hidden="true" className="size-5 shrink-0 text-muted rtl:rotate-180" />
      </Link>
    </li>
  );
}

function PendingInvitations() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const pending = useQuery({
    queryKey: invitationsKey,
    queryFn: () => api.invitations.listPending(),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.invitations.revoke(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invitationsKey }),
  });
  const items = pending.data?.items ?? [];
  if (items.length === 0) return null;
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
  return (
    <Card>
      <CardTitle>{t('members.pendingTitle')}</CardTitle>
      <FormError error={revoke.error} />
      <ul className="mt-2 flex flex-col gap-3">
        {items.map((inv) => (
          <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex flex-col">
              <span className="font-medium">{inv.memberDisplayName}</span>
              <span className="text-sm text-muted">
                {t('members.pendingLine', {
                  email: inv.email,
                  date: date.format(new Date(inv.expiresAt)),
                })}
              </span>
            </span>
            <Button
              variant="ghost"
              onClick={() => revoke.mutate(inv.id)}
              disabled={revoke.isPending}
            >
              {t('members.cancelInvite')}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function MembersPage() {
  const { t } = useTranslation();
  const me = useMe();
  const canManage = useCan('members.manage');
  const canInvite = useCan('members.invite');
  const members = useMembers();
  const limits = useLimits();
  const over = new Set(limits.data?.items.filter((l) => l.over).map((l) => l.memberId));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('members.title')}
        back="/family"
        action={
          canManage && (
            <Link to="/members/new" className={buttonVariants({ variant: 'small' })}>
              {t('members.add')}
            </Link>
          )
        }
      />
      {members.isError && <FormError error={members.error} />}
      <ListGroup>
        {members.data?.pages
          .flatMap((page) => page.items)
          .map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              isMe={member.id === me.data?.member.id}
              overLimit={over.has(member.id)}
            />
          ))}
      </ListGroup>
      {members.hasNextPage && (
        <Button variant="ghost" block onClick={() => void members.fetchNextPage()}>
          {t('members.loadMore')}
        </Button>
      )}
      {canInvite && <PendingInvitations />}
    </div>
  );
}
